/**
 * Forecast card header: title + part count + snapshot freshness on the left;
 * quick search, Refresh (snapshot installs only), Export and Filters on the
 * right. CR-6448: button order matches SO Headers — Refresh → Export → Filters.
 */
import { CardTitle } from '../../../ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../../ui/dropdown-menu';
import { Download, Filter, Loader2, RefreshCw, Search, TrendingUp } from 'lucide-react';
import { ForecastRefreshStatus } from './useForecastSnapshotRefresh';

interface ForecastToolbarProps {
  partsCount: number;
  refreshStatus: ForecastRefreshStatus | null;
  refreshing: boolean;
  onRefresh: () => void;
  exporting: boolean;
  onExport: (format: 'csv' | 'xlsx') => void;
  quickSearch: string;
  quickSearchPlaceholder: string;
  onQuickSearchChange: (value: string) => void;
  showFilters: boolean;
  onToggleFilters: () => void;
  activeFilterCount: number;
}

const OUTLINE_BUTTON = 'px-3 py-2 rounded-lg border border-border text-sm flex items-center gap-2 hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors';

export function ForecastToolbar({
  partsCount, refreshStatus, refreshing, onRefresh, exporting, onExport,
  quickSearch, quickSearchPlaceholder, onQuickSearchChange, showFilters,
  onToggleFilters, activeFilterCount,
}: ForecastToolbarProps) {
  const lastRefreshed = refreshStatus?.last_refreshed_at
    ? new Date(refreshStatus.last_refreshed_at).toLocaleString()
    : null;
  return (
    <div className="flex items-center justify-between">
      <div className="flex flex-col">
        <div className="flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-indigo-600" />
          <CardTitle>Forecast Schedule</CardTitle>
          <span className="text-sm text-muted-foreground ml-2">
            ({partsCount.toLocaleString()} parts)
          </span>
        </div>
        {refreshStatus?.enabled && lastRefreshed && (
          <span className="text-[11px] text-muted-foreground ml-7 mt-0.5">
            Last refreshed {lastRefreshed}
            {refreshStatus.last_status === 'failed' && (
              <span className="text-red-600 ml-2">· last refresh failed</span>
            )}
          </span>
        )}
      </div>
      <div className="flex items-center gap-2">
        {/* Quick part search — debounced by the caller, applies independently
            of the Filters drawer. Matches the customer part number, the
            supplier's own reference, OR a contributing open PO number (API
            param part_search): a code off a delivery note may be any of them. */}
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <input
            type="text"
            value={quickSearch}
            placeholder={quickSearchPlaceholder}
            aria-label="Quick search by part number, supplier part number or PO number"
            onChange={(e) => onQuickSearchChange(e.target.value)}
            className="pl-8 pr-3 py-2 text-sm border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary w-72"
          />
        </div>
        {refreshStatus?.enabled && (
          <button
            onClick={onRefresh}
            disabled={refreshing}
            title={lastRefreshed ? `Last refreshed ${lastRefreshed}` : 'Trigger an on-demand snapshot refresh'}
            className={OUTLINE_BUTTON}
          >
            {refreshing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            Refresh
          </button>
        )}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button disabled={exporting} className={OUTLINE_BUTTON}>
              {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              Export
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => onExport('csv')}>Export as CSV</DropdownMenuItem>
            <DropdownMenuItem onClick={() => onExport('xlsx')}>Export as Excel</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <button
          onClick={onToggleFilters}
          className={`px-4 py-2 rounded-lg border text-sm font-medium flex items-center gap-2 transition-colors ${
            showFilters ? 'bg-primary text-primary-foreground border-primary' : 'border-border hover:bg-muted'
          }`}
        >
          <Filter className="h-4 w-4" />
          Filters
          {activeFilterCount > 0 && (
            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
              showFilters ? 'bg-primary-foreground/20 text-primary-foreground' : 'bg-primary text-primary-foreground'
            }`}>
              {activeFilterCount}
            </span>
          )}
        </button>
      </div>
    </div>
  );
}
